import { AppApiError } from '../auth/AppApiClient';
import type { BrowserIdentityService } from '../auth/BrowserIdentityService';
import { formatDate, t } from '../i18n';
import type { MatchMode, MatchPlaylist, MatchRegion, MatchmakingSearchRequest, QueueTicket } from './MatchmakingContracts';
import type { OnlineSettings } from './OnlineSettings';
import './online.css';

export interface MatchmakingViewCallbacks {
  readonly onBack: () => void;
  readonly onLocal?: () => void;
  readonly onFreeplay?: () => void;
  readonly onReserved: (ticket: QueueTicket) => void;
}

export class MatchmakingView {
  readonly element = document.createElement('main');
  readonly #identity: BrowserIdentityService;
  readonly #callbacks: MatchmakingViewCallbacks;
  readonly #status: HTMLElement;
  readonly #rating: HTMLElement;
  readonly #availability: HTMLElement;
  readonly #search: HTMLButtonElement;
  readonly #cancel: HTMLButtonElement;
  readonly #mode: HTMLSelectElement;
  readonly #format: HTMLSelectElement;
  readonly #regionMode: HTMLSelectElement;
  readonly #regionField: HTMLElement;
  readonly #regionControls: HTMLElement;
  readonly #regionFixed: HTMLElement;
  readonly #regionOptions: HTMLElement;
  #ticket: QueueTicket | null = null;
  #regions: readonly MatchRegion[] = [];
  #selectedRegions = new Set<string>();
  #regionCatalogInitialized = false;
  #regionLatencies = new Map<string, number>();
  #lastRegionProbeAt = 0;
  #probingRegions = false;
  #timer: ReturnType<typeof setInterval> | null = null;
  #disposed = false;
  #busy = false;
  #initialized = false;
  #requestRevision = 0;
  #polling = false;
  #unavailable = false;
  #availabilityState: Pick<OnlineSettings, 'casualEnabled' | 'rankedEnabled'> | null = null;
  #queueClosedNotice = false;
  readonly #failedReservationId: string | null;

  constructor(identity: BrowserIdentityService, callbacks: MatchmakingViewCallbacks,
    initialPlaylist?: Pick<MatchPlaylist, 'mode' | 'format'>, failedReservationId: string | null = null) {
    this.#identity = identity;
    this.#callbacks = callbacks;
    this.#failedReservationId = failedReservationId;
    this.element.className = 'shell match-setup-screen matchmaking-screen';
    this.element.innerHTML = `
      <div id="home-scene" class="home-scene match-setup-scene" aria-hidden="true"></div>
      <div class="match-setup-screen__wash" aria-hidden="true"></div>
      <header class="match-setup-header">
        <div class="match-setup-header__identity">
          <button class="match-setup-back back-button" type="button" aria-label="${t('online:matchmaking.backAria')}">←</button>
          <div><span class="match-setup-kicker">${t('online:matchmaking.kicker')}</span><h1>${t('online:matchmaking.title')}</h1>
            <p>${t('online:matchmaking.subtitle')}</p></div>
        </div>
        <div class="match-format-picker" role="group" aria-label="${t('online:matchmaking.gameModeAria')}">
          <button class="match-format-button" data-play-mode="local" type="button">${t('online:matchmaking.localMatch')}</button>
          <button class="match-format-button is-active" type="button" aria-current="page">${t('online:matchmaking.onlineMatch')}</button>
          <button class="match-format-button" data-play-mode="freeplay" type="button">${t('online:matchmaking.freeplay')}</button>
        </div>
      </header>
      <section class="matchmaking-layout">
        <section class="matchmaking-card" aria-labelledby="matchmaking-title">
          <header class="matchmaking-card__header"><span>${t('online:matchmaking.publicSearch')}</span><h2 id="matchmaking-title">${t('online:matchmaking.findMatch')}</h2>
            <p>${t('online:matchmaking.description')}</p></header>
          <div class="matchmaking-fields">
            <label><span>${t('online:matchmaking.modeLabel')}</span><select data-field="mode">
              <option value="casual">${t('online:matchmaking.casual')}</option><option value="ranked">${t('online:matchmaking.ranked')}</option></select>
              <small>${t('online:matchmaking.modeHelp')}</small></label>
            <label><span>${t('online:matchmaking.formatLabel')}</span><select data-field="format"><option value="1">1v1</option><option value="2">2v2</option>
              <option value="3">3v3</option></select><small>${t('online:matchmaking.formatHelp')}</small></label>
            <div class="matchmaking-region-field">
              <div class="matchmaking-region-controls">
                <label><span>${t('online:matchmaking.region.label')}</span><select data-field="region-mode">
                  <option value="automatic">${t('online:matchmaking.region.automatic')}</option>
                  <option value="manual">${t('online:matchmaking.region.manual')}</option>
                </select><small>${t('online:matchmaking.region.help')}</small></label>
                <div class="matchmaking-region-list" data-region-options role="group" aria-label="${t('online:matchmaking.region.availableAria')}"></div>
              </div>
              <div class="matchmaking-region-fixed" hidden></div>
            </div>
          </div>
          <div class="matchmaking-feedback">
            <p class="matchmaking-rating"></p><p class="matchmaking-availability" role="status"></p>
            <p class="matchmaking-status" role="status">${t('online:matchmaking.checking')}</p>
          </div>
          <div class="matchmaking-actions"><button class="match-setup-start" data-action="search" type="button">${t('online:matchmaking.searchMatch')} <span aria-hidden="true">›</span></button>
            <button class="matchmaking-cancel" data-action="cancel" type="button" hidden>${t('online:matchmaking.cancelSearch')}</button></div>
        </section>
        <aside class="matchmaking-guide" aria-label="${t('online:matchmaking.guideAria')}">
          <div><span aria-hidden="true">◇</span><strong>${t('online:matchmaking.guide.casualTitle')}</strong><p>${t('online:matchmaking.guide.casualDescription')}</p></div>
          <div><span aria-hidden="true">✦</span><strong>${t('online:matchmaking.guide.rankedTitle')}</strong><p>${t('online:matchmaking.guide.rankedDescription')}</p></div>
          <div><span aria-hidden="true">↗</span><strong>${t('online:matchmaking.guide.connectionTitle')}</strong><p>${t('online:matchmaking.guide.connectionDescription')}</p></div>
        </aside>
      </section>`;
    this.#status = this.element.querySelector('.matchmaking-status')!;
    this.#rating = this.element.querySelector('.matchmaking-rating')!;
    this.#availability = this.element.querySelector('.matchmaking-availability')!;
    this.#search = this.element.querySelector('[data-action="search"]')!;
    this.#cancel = this.element.querySelector('[data-action="cancel"]')!;
    this.#mode = this.element.querySelector('[data-field="mode"]')!;
    this.#format = this.element.querySelector('[data-field="format"]')!;
    this.#regionMode = this.element.querySelector('[data-field="region-mode"]')!;
    this.#regionField = this.element.querySelector('.matchmaking-region-field')!;
    this.#regionControls = this.element.querySelector('.matchmaking-region-controls')!;
    this.#regionFixed = this.element.querySelector('.matchmaking-region-fixed')!;
    this.#regionOptions = this.element.querySelector('[data-region-options]')!;
    if (initialPlaylist) {
      this.#mode.value = initialPlaylist.mode;
      this.#format.value = String(initialPlaylist.format);
    }
    this.element.querySelector('.back-button')?.addEventListener('click', () => this.#leave(callbacks.onBack));
    this.element.querySelector('[data-play-mode="local"]')?.addEventListener('click', () => {
      if (callbacks.onLocal) this.#leave(callbacks.onLocal);
    });
    this.element.querySelector('[data-play-mode="freeplay"]')?.addEventListener('click', () => {
      if (callbacks.onFreeplay) this.#leave(callbacks.onFreeplay);
    });
    this.#search.addEventListener('click', () => void this.#enqueue());
    this.#cancel.addEventListener('click', () => void this.#cancelSearch());
    this.#mode.addEventListener('change', () => {
      this.#queueClosedNotice = false;
      void this.#refreshRating();
    });
    this.#format.addEventListener('change', () => void this.#refreshRating());
    this.#regionMode.addEventListener('change', () => this.#render());
    this.#regionOptions.addEventListener('change', (event) => {
      const input = event.target as HTMLInputElement | null;
      const regionId = input?.dataset.regionId;
      if (!regionId || !this.#regions.some((region) => region.id === regionId && region.manualSelectable !== false)) return;
      if (input.checked) this.#selectedRegions.add(regionId); else this.#selectedRegions.delete(regionId);
      this.#render();
    });
    void this.#initialize(Boolean(initialPlaylist));
  }

  dispose(): void {
    this.#disposed = true;
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = null;
  }

  async #initialize(autoEnqueue: boolean): Promise<void> {
    await this.#refreshStatus();
    if (this.#disposed) return;
    this.#initialized = true;
    this.#render();
    if (autoEnqueue && !this.#ticket && !this.#unavailable && this.#modeEnabled(this.#queue().mode)) {
      void this.#enqueue();
    }
    await this.#refreshRating();
    if (this.#disposed) return;
    this.#timer = setInterval(() => void this.#refreshStatus(), 3_000);
  }

  #leave(destination: () => void): void {
    if (!this.#initialized || this.#busy) return;
    if (this.#ticket && this.#ticket.state !== 'admitted') {
      void this.#cancelSearch().then(() => { if (!this.#ticket) destination(); });
      return;
    }
    destination();
  }

  #modeEnabled(mode: MatchMode): boolean {
    return mode === 'casual' ? this.#availabilityState?.casualEnabled === true
      : this.#availabilityState?.rankedEnabled === true;
  }

  #queue(): Pick<MatchPlaylist, 'mode' | 'format'> {
    return { mode: this.#mode.value === 'ranked' ? 'ranked' : 'casual',
      format: Number(this.#format.value) as MatchPlaylist['format'] };
  }

  #searchRequest(): MatchmakingSearchRequest | null {
    const queue = this.#queue();
    const selectable = this.#regions.filter((region) => region.manualSelectable !== false);
    const regionPreference = selectable.length && this.#regionMode.value === 'manual'
      ? { mode: 'manual' as const, regionIds: selectable.map((region) => region.id).filter((id) => this.#selectedRegions.has(id)) }
      : { mode: 'automatic' as const };
    if (regionPreference.mode === 'manual' && !regionPreference.regionIds.length) return null;
    return { ...queue, regionPreference,
      regionLatenciesMs: Object.fromEntries(this.#regions.flatMap((region) => {
        const latency = this.#regionLatencies.get(region.id);
        return latency === undefined ? [] : [[region.id, latency] as const];
      })) };
  }

  async #enqueue(): Promise<void> {
    if (this.#busy || !this.#initialized || this.#unavailable || this.#ticket) return;
    const search = this.#searchRequest();
    if (!search) {
      this.#status.textContent = t('online:matchmaking.region.selectAtLeastOne');
      this.#render();
      return;
    }
    if (!this.#modeEnabled(search.mode)) {
      this.#render();
      return;
    }
    if (search.mode === 'ranked' && this.#identity.snapshot.kind !== 'authenticated') {
      this.#status.textContent = t('online:matchmaking.status.rankedAccountRequired');
      return;
    }
    this.#busy = true;
    this.#queueClosedNotice = false;
    this.#requestRevision += 1;
    this.#render();
    try {
      const { ticket } = await this.#identity.enqueueMatchmaking(search);
      if (this.#disposed) return;
      this.#ticket = ticket;
      this.#render();
    } catch (error) {
      if (!this.#disposed) {
        this.#status.textContent = describeQueueError(error);
      }
    } finally {
      this.#busy = false;
      if (!this.#disposed) this.#render();
    }
  }

  async #cancelSearch(successMessage = t('online:matchmaking.status.cancelled')): Promise<void> {
    if (this.#busy || !this.#ticket) return;
    this.#busy = true;
    this.#requestRevision += 1;
    this.#render();
    try {
      await this.#identity.cancelMatchmaking();
      if (this.#disposed) return;
      this.#ticket = null;
      this.#queueClosedNotice = false;
      this.#status.textContent = successMessage;
    } catch (error) {
      if (!this.#disposed) this.#status.textContent = describeQueueError(error);
    } finally {
      this.#busy = false;
      if (!this.#disposed) this.#render();
    }
  }

  async #refreshStatus(): Promise<void> {
    if (this.#busy || this.#polling) return;
    this.#polling = true;
    const revision = this.#requestRevision;
    try {
      const response = await this.#identity.getMatchmaking();
      if (this.#disposed || revision !== this.#requestRevision) return;
      const { ticket, banUntil } = response;
      const previousTicket = this.#ticket;
      this.#availabilityState = response.availability;
      this.#setRegions(response.regions);
      this.#unavailable = false;
      this.#ticket = ticket;
      if (!ticket && previousTicket?.state === 'queued' && !this.#modeEnabled(previousTicket.search.mode)) {
        this.#queueClosedNotice = true;
        this.#status.textContent = t('online:matchmaking.status.queueClosed');
      } else if (!ticket && this.#queueClosedNotice) {
        this.#status.textContent = t('online:matchmaking.status.queueClosed');
      } else if (banUntil && new Date(banUntil).getTime() > Date.now()) {
        this.#status.textContent = t('online:matchmaking.status.bannedUntil', {
          time: formatDate(new Date(banUntil), { hour: 'numeric', minute: '2-digit' }),
        });
      } else if (ticket?.state === 'queued') {
        this.#status.textContent = t('online:matchmaking.status.searching', {
          format: ticket.search.format,
          mode: t(`online:matchmaking.${ticket.search.mode}`),
        });
      } else if (ticket?.state === 'reserved') {
        this.#status.textContent = ticket.playlist
          ? t('online:matchmaking.status.foundEnteringRegion', { region: this.#regionLabel(ticket.playlist.region) })
          : t('online:matchmaking.status.foundEntering');
        if (ticket.roomId && ticket.reservationId && ticket.playlist && ticket.connection
          && ticket.reservationId !== this.#failedReservationId) {
          this.#callbacks.onReserved(ticket);
        } else if (ticket.reservationId === this.#failedReservationId) {
          this.#status.textContent = t('online:matchmaking.status.reservationFailed');
          await this.#cancelSearch(t('online:matchmaking.status.previousReservationClosed'));
          return;
        }
      } else if (ticket?.state === 'admitted') {
        this.#status.textContent = t('online:matchmaking.status.alreadyStarted');
      } else this.#status.textContent = t('online:matchmaking.status.choosePlaylist');
      this.#render();
      if (Date.now() - this.#lastRegionProbeAt > 30_000) void this.#probeRegions();
    } catch (error) {
      if (!this.#disposed && revision === this.#requestRevision) {
        if (error instanceof AppApiError && (error.status === 404 || error.status === 503)) this.#unavailable = true;
        this.#availabilityState = null;
        this.#status.textContent = describeQueueError(error);
        this.#render();
      }
    } finally {
      this.#polling = false;
    }
  }

  async #refreshRating(): Promise<void> {
    if (this.#ticket) return;
    const { mode, format } = this.#queue();
    try {
      const { rating } = await this.#identity.getOnlineRating(mode, format);
      if (!this.#disposed && !this.#ticket && this.#queue().mode === mode && this.#queue().format === format) {
        this.#rating.textContent = mode === 'ranked'
          ? t('online:matchmaking.rating.ranked', { rating: Math.round(rating.rating), count: rating.games })
          : t('online:matchmaking.rating.casual', { count: rating.games });
      }
    } catch {
      if (!this.#disposed) this.#rating.textContent = '';
    }
  }

  #render(): void {
    const casual = this.#mode.querySelector<HTMLOptionElement>('option[value="casual"]');
    const ranked = this.#mode.querySelector<HTMLOptionElement>('option[value="ranked"]');
    if (casual) casual.disabled = !this.#modeEnabled('casual');
    if (ranked) ranked.disabled = !this.#modeEnabled('ranked');
    if (!this.#ticket && !this.#modeEnabled(this.#queue().mode)) {
      if (this.#modeEnabled('casual')) this.#mode.value = 'casual';
      else if (this.#modeEnabled('ranked')) this.#mode.value = 'ranked';
    }
    const availability = this.#availabilityState;
    this.#availability.textContent = !availability
      ? t('online:matchmaking.availability.unavailable')
      : !availability.casualEnabled && !availability.rankedEnabled
        ? t('online:matchmaking.availability.allDisabled')
        : this.#ticket ? '' : !availability.casualEnabled
          ? t('online:matchmaking.availability.casualDisabled')
          : !availability.rankedEnabled
            ? t('online:matchmaking.availability.rankedDisabled') : '';
    this.#availability.hidden = !this.#availability.textContent;
    this.#search.hidden = false;
    this.#cancel.hidden = this.#ticket === null || this.#ticket.state === 'admitted';
    this.#search.disabled = this.#busy || !this.#initialized || this.#unavailable
      || this.#ticket !== null || !this.#modeEnabled(this.#queue().mode) || !this.#regions.length
      || !this.#regionField.hidden && this.#regionMode.value === 'manual' && !this.#selectedRegions.size;
    this.#search.innerHTML = this.#ticket?.state === 'reserved'
      ? t('online:matchmaking.button.entering')
      : this.#ticket ? t('online:matchmaking.button.searching')
        : `${t('online:matchmaking.button.search')} <span aria-hidden="true">›</span>`;
    this.#cancel.disabled = this.#busy;
    this.#mode.disabled = this.#ticket !== null || this.#busy;
    this.#format.disabled = this.#ticket !== null || this.#busy;
    this.#regionMode.disabled = this.#ticket !== null || this.#busy || !this.#regions.length;
    for (const input of this.#regionOptions.querySelectorAll<HTMLInputElement>('input[data-region-id]')) {
      input.disabled = this.#ticket !== null || this.#busy || this.#regionMode.value !== 'manual';
    }
    const back = this.element.querySelector<HTMLButtonElement>('.back-button');
    if (back) back.disabled = !this.#initialized || this.#busy;
    for (const selector of ['[data-play-mode="local"]', '[data-play-mode="freeplay"]']) {
      const button = this.element.querySelector<HTMLButtonElement>(selector);
      if (button) button.disabled = !this.#initialized || this.#busy;
    }
    if (this.#ticket) {
      this.#mode.value = this.#ticket.search.mode;
      this.#format.value = String(this.#ticket.search.format);
      if (!this.#regionControls.hidden) this.#regionMode.value = this.#ticket.search.regionPreference.mode;
      if (this.#ticket.search.regionPreference.mode === 'manual') {
        this.#selectedRegions = new Set(this.#ticket.search.regionPreference.regionIds);
      }
    }
  }

  #setRegions(regions: readonly MatchRegion[]): void {
    const normalized = regions.filter((region, index) => /^[a-z0-9-]{1,32}$/.test(region.id)
      && region.label.length > 0 && regions.findIndex((candidate) => candidate.id === region.id) === index);
    const available = new Set(normalized.filter((region) => region.manualSelectable !== false).map((region) => region.id));
    const fixedRegion = normalized.length === 1 && available.size === 0 ? normalized[0]! : null;
    this.#regionField.hidden = normalized.length === 0;
    this.#regionControls.hidden = fixedRegion !== null || normalized.length === 0;
    this.#regionFixed.hidden = fixedRegion === null;
    this.#regionFixed.textContent = fixedRegion
      ? t('online:matchmaking.region.serverLocation', { region: fixedRegion.label })
      : '';
    if (available.size === 0) this.#regionMode.value = 'automatic';
    const changed = JSON.stringify(normalized) !== JSON.stringify(this.#regions);
    if (!changed) return;
    this.#regions = normalized;
    if (!this.#regionCatalogInitialized) {
      this.#selectedRegions = new Set(available);
      this.#regionCatalogInitialized = true;
    } else {
      this.#selectedRegions = new Set([...this.#selectedRegions].filter((region) => available.has(region)));
    }
    this.#regionOptions.innerHTML = normalized.filter((region) => region.manualSelectable !== false).map((region) => `<label class="matchmaking-region-option">
      <input type="checkbox" data-region-id="${region.id}" ${this.#selectedRegions.has(region.id) ? 'checked' : ''}>
      <span>${escapeHtml(region.label)}</span><small data-region-latency="${region.id}">—</small></label>`).join('');
    this.#lastRegionProbeAt = 0;
  }

  async #probeRegions(): Promise<void> {
    if (this.#probingRegions || !this.#regions.length) return;
    this.#probingRegions = true;
    this.#lastRegionProbeAt = Date.now();
    try {
      const results = await Promise.all(this.#regions.filter((region) => region.manualSelectable !== false)
        .map(async (region) => [region.id, await probeRegion(region)] as const));
      if (this.#disposed) return;
      for (const [regionId, latency] of results) {
        if (latency === null) this.#regionLatencies.delete(regionId); else this.#regionLatencies.set(regionId, latency);
        const label = this.#regionOptions.querySelector<HTMLElement>(`[data-region-latency="${regionId}"]`);
        if (label) label.textContent = latency === null ? t('online:matchmaking.region.unreachable')
          : t('online:matchmaking.region.latency', { latency });
      }
    } finally { this.#probingRegions = false; }
  }

  #regionLabel(regionId: string): string {
    return this.#regions.find((region) => region.id === regionId)?.label ?? regionId;
  }
}

async function probeRegion(region: MatchRegion): Promise<number | null> {
  if (!region.probeUrl) return null;
  const samples: number[] = [];
  for (let index = 0; index < 3; index += 1) {
    const started = performance.now();
    try {
      const response = await fetch(region.probeUrl, { method: 'GET', cache: 'no-store', credentials: 'omit',
        mode: 'cors', signal: AbortSignal.timeout(2_500) });
      if (!response.ok) return null;
      samples.push(performance.now() - started);
    } catch { return null; }
  }
  samples.sort((a, b) => a - b);
  return Math.max(0, Math.round(samples[1]!));
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]!);
}

function describeQueueError(error: unknown): string {
  if (error instanceof AppApiError) {
    if (error.status === 404) return t('online:matchmaking.errors.serverUnavailable');
    if (error.status === 503) return t('online:matchmaking.errors.temporarilyUnavailable');
    if (error.code === 'ranked-account-required') return t('online:matchmaking.status.rankedAccountRequired');
    if (error.code === 'queue-banned') {
      const until = error.details?.until;
      return until
        ? t('online:matchmaking.status.bannedUntil', {
          time: formatDate(new Date(until), { hour: 'numeric', minute: '2-digit' }),
        })
        : t('online:matchmaking.errors.queueBanned');
    }
    if (error.code === 'already-queued') return t('online:matchmaking.errors.alreadyQueued');
    if (error.code === 'playlist-disabled') return t('online:matchmaking.errors.playlistDisabled');
  }
  return t('online:matchmaking.errors.generic');
}
